/**
 * Parental gate (docs/GAME_DESIGN.md § 11, docs/MONETIZATION.md): shown before
 * any purchase, restore or external link. Two steps an adult does easily and a
 * young child can't do by tapping around:
 *
 *  1. press and hold a button for HOLD_MS (no countdown text, just a fill), then
 *  2. type the number shown in words ("forty-seven" / "sewe-en-veertig") as digits.
 *
 * After GATE_MAX_FAILURES wrong answers in a row the gate locks for
 * GATE_LOCKOUT_MS (saved in storage, so closing and reopening the gate or
 * reloading the page doesn't reset it), which stops a child from simply
 * guessing until it opens.
 *
 * This is a guard against accidental taps and curious young children, not a
 * security barrier: many school-age children can read number names. The real
 * barrier for money is the platform store (StoreKit with Ask to Buy, Google
 * Play with Family Link purchase approval), see docs/MONETIZATION.md § 4.
 *
 * This module is the pure logic; the UI owns the hold gesture and the input.
 */
import { Rng, type Locale } from '@dropstack/core';
import { getAppStorage, isFiniteNumber, isRecord, STORAGE_KEYS, type JsonStore } from '../state/storage.ts';

/** How long the hold button must stay pressed. */
export const HOLD_MS = 3000;

/** Challenge answers are drawn from this range, skipping round tens (easy to guess from one word). */
export const GATE_MIN = 21;
export const GATE_MAX = 99;

export interface GateChallenge {
  /** The number to type, 21–99, never a multiple of 10. */
  answer: number;
  /** The number written out in the challenge's language. */
  words: string;
  locale: Locale;
}

const EN_UNITS = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen',
] as const;
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'] as const;

const AF_UNITS = [
  'nul', 'een', 'twee', 'drie', 'vier', 'vyf', 'ses', 'sewe', 'agt', 'nege',
  'tien', 'elf', 'twaalf', 'dertien', 'veertien', 'vyftien', 'sestien', 'sewentien', 'agttien', 'negentien',
] as const;
const AF_TENS = ['', '', 'twintig', 'dertig', 'veertig', 'vyftig', 'sestig', 'sewentig', 'tagtig', 'negentig'] as const;

/**
 * Write 0–99 in words.
 * English: 47 → "forty-seven". Afrikaans puts the unit first, joined with
 * "-en-" (AWS spelling): 47 → "sewe-en-veertig", 21 → "een-en-twintig".
 * @throws RangeError outside 0–99 or for non-integers.
 */
export function numberToWords(n: number, locale: Locale): string {
  if (!Number.isInteger(n) || n < 0 || n > 99) throw new RangeError(`numberToWords: expected an integer 0–99, got ${n}`);
  const units = locale === 'af' ? AF_UNITS : EN_UNITS;
  const tens = locale === 'af' ? AF_TENS : EN_TENS;
  if (n < 20) return units[n]!;
  const ten = tens[Math.floor(n / 10)]!;
  const unit = n % 10;
  if (unit === 0) return ten;
  return locale === 'af' ? `${units[unit]!}-en-${ten}` : `${ten}-${units[unit]!}`;
}

/** A challenge for `locale`. Deterministic per seed (pass randomSeed() in the app). */
export function makeGateChallenge(locale: Locale, seed: number): GateChallenge {
  const rng = new Rng(seed);
  // Tens 2–9 and a non-zero unit: every value in 21–99 except the round tens, uniformly.
  const answer = rng.int(Math.floor(GATE_MIN / 10), Math.floor(GATE_MAX / 10)) * 10 + rng.int(1, 9);
  return { answer, words: numberToWords(answer, locale), locale };
}

/**
 * True when `input` is the challenge's number in digits. Surrounding and inner
 * whitespace is ignored ("4 7" is fine); words, signs and decimals are not accepted.
 */
export function checkGateAnswer(challenge: Pick<GateChallenge, 'answer'>, input: string): boolean {
  const digits = input.normalize('NFKC').replace(/\s+/g, '');
  return /^\d{1,3}$/.test(digits) && Number(digits) === challenge.answer;
}

/** Hold progress 0–1 for a press that has lasted `heldMs` (for the fill animation). */
export function holdProgress(heldMs: number): number {
  return Math.min(1, Math.max(0, heldMs / HOLD_MS));
}

// ───────────── Lockout ─────────────

/** Wrong answers in a row that lock the gate. */
export const GATE_MAX_FAILURES = 3;

/** How long the gate stays locked after GATE_MAX_FAILURES wrong answers. */
export const GATE_LOCKOUT_MS = 60_000;

/** Persisted lockout state (storage key STORAGE_KEYS.gate). */
export interface GateLock {
  /** Wrong answers since the last pass or lockout. */
  failures: number;
  /** Epoch ms until which the gate refuses to open; null when not locked. */
  lockedUntil: number | null;
}

export const OPEN_GATE_LOCK: Readonly<GateLock> = Object.freeze({ failures: 0, lockedUntil: null });

/** Milliseconds the gate stays locked from `now` (0 when it is open). */
export function gateLockRemaining(lock: GateLock, now: number): number {
  return lock.lockedUntil === null ? 0 : Math.max(0, lock.lockedUntil - now);
}

/** The state after a wrong answer at `now`: the GATE_MAX_FAILURES-th locks the gate. */
export function recordGateFailure(lock: GateLock, now: number): GateLock {
  if (gateLockRemaining(lock, now) > 0) return lock;
  const failures = lock.failures + 1;
  return failures >= GATE_MAX_FAILURES ? { failures: 0, lockedUntil: now + GATE_LOCKOUT_MS } : { failures, lockedUntil: null };
}

/**
 * The saved lockout state. A lock that has run out (or a clock that moved
 * backwards more than a lockout, which would otherwise lock forever) reads as
 * open.
 */
export function loadGateLock(now: number, store: JsonStore = getAppStorage()): GateLock {
  const lock = store.get(STORAGE_KEYS.gate, decodeGateLock) ?? { ...OPEN_GATE_LOCK };
  const remaining = gateLockRemaining(lock, now);
  if (lock.lockedUntil !== null && (remaining === 0 || remaining > GATE_LOCKOUT_MS)) return { failures: 0, lockedUntil: null };
  return lock;
}

export function saveGateLock(lock: GateLock, store: JsonStore = getAppStorage()): void {
  store.set(STORAGE_KEYS.gate, lock);
}

function decodeGateLock(raw: unknown): GateLock | null {
  if (!isRecord(raw)) return null;
  const failures = isFiniteNumber(raw.failures) && Number.isInteger(raw.failures) ? Math.min(Math.max(raw.failures, 0), GATE_MAX_FAILURES) : 0;
  const lockedUntil = isFiniteNumber(raw.lockedUntil) ? raw.lockedUntil : null;
  return { failures, lockedUntil };
}
